import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, X, RefreshCw, AlertTriangle, Pencil, Bot } from "lucide-react";
import { toast } from "sonner";

const INTENT = { interested: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400 border-emerald-500/25", question: "bg-blue-500/12 text-blue-700 dark:text-blue-400 border-blue-500/25", objection: "bg-amber-500/12 text-amber-700 dark:text-amber-400 border-amber-500/25", not_interested: "bg-muted text-muted-foreground border-border", unsubscribe: "bg-rose-500/12 text-rose-700 dark:text-rose-400 border-rose-500/25", out_of_office: "bg-muted text-muted-foreground border-border", other: "bg-muted text-muted-foreground border-border" };

export default function AiReplyCard({ thread, accountId, onSent }) {
  const [draft, setDraft] = useState(null);
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasInbound = thread?.inbound > 0;
  const load = useCallback(async () => {
    if (!thread?.thread_id) return;
    const { data } = await api.get("/ai-replies/drafts", { params: { thread_id: thread.thread_id } });
    const d = data.find((x) => x.status === "pending" || x.status === "failed") || data[0] || null;
    setDraft(d); setBody(d?.body || ""); setEditing(false);
  }, [thread?.thread_id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); onSent?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const generate = () => act(() => api.post("/ai-replies/generate", { thread_id: thread.thread_id }), "AI drafted a reply");
  const send = () => act(() => api.post(`/ai-replies/drafts/${draft.id}/send`, { body, account_id: accountId || null }), "Reply sent");
  const dismiss = () => act(() => api.post(`/ai-replies/drafts/${draft.id}/dismiss`), "Draft dismissed");

  if (!hasInbound) return null;
  if (!draft || !["pending", "failed"].includes(draft.status)) {
    return (
      <div className="px-6 pb-2 flex items-center justify-between gap-3 text-xs text-muted-foreground" data-testid="ai-reply-empty">
        <span className="flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" />{draft?.status === "auto_sent" ? "AI replied autonomously to the last message." : draft?.status === "sent" ? "AI draft was sent." : "No AI draft for this thread yet."}</span>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={generate} data-testid="ai-reply-generate"><Sparkles className="w-3.5 h-3.5" />{busy ? "Drafting..." : "Draft reply with AI"}</Button>
      </div>
    );
  }
  return (
    <div className="mx-6 mb-3 rounded-lg border border-primary/30 bg-primary/[0.04] shadow-[var(--shadow-card)]" data-testid="ai-reply-card">
      <div className="px-4 py-2 border-b border-primary/15 flex items-center gap-2 text-xs flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-primary" /><span className="font-semibold">AI suggested reply</span>
        <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-px rounded-full border ${INTENT[draft.intent] || INTENT.other}`} data-testid="ai-reply-intent">{draft.intent.replace("_", " ")}</span>
        <span className="mono text-muted-foreground">{Math.round((draft.confidence || 0) * 100)}% confident</span>
        {draft.escalate && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400" data-testid="ai-reply-escalate"><AlertTriangle className="w-3 h-3" />Needs a human: {draft.escalate_reason}</span>}
        {draft.status === "failed" && <span className="text-rose-500">Send failed: {draft.error}</span>}
      </div>
      {draft.summary && <div className="px-4 pt-2 text-[11px] text-muted-foreground italic" data-testid="ai-reply-summary">Prospect: {draft.summary}</div>}
      <div className="px-4 py-2">
        {editing ? <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="text-sm bg-card" data-testid="ai-reply-body" /> : <div className="text-sm whitespace-pre-wrap" data-testid="ai-reply-preview">{body}</div>}
      </div>
      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing((v) => !v)} data-testid="ai-reply-edit"><Pencil className="w-3 h-3" />{editing ? "Preview" : "Edit"}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy} onClick={generate} data-testid="ai-reply-regenerate"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />Regenerate</Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={dismiss} data-testid="ai-reply-dismiss"><X className="w-3 h-3" />Dismiss</Button>
          <Button size="sm" className="h-7 text-xs" disabled={busy || !body.trim()} onClick={send} data-testid="ai-reply-send"><Send className="w-3 h-3" />{busy ? "Sending..." : "Approve & send"}</Button>
        </div>
      </div>
    </div>
  );
}
